'use client'
import clsx from 'clsx'
import { forwardRef, useEffect, useRef, useState, type HTMLAttributes } from 'react'
import { Alert, type AlertType, type AlertVariant } from '../alert'
import { Drawer } from '../drawer'

type ToastProps = HTMLAttributes<HTMLDivElement> & {
    type?: AlertType
    variant?: AlertVariant
    duration?: number
    onClose?: () => void
}

const Toast = forwardRef<HTMLDivElement, ToastProps>((props, ref) => {
    const { children, className, type, variant, duration, ...otherProps } = props

    const [isOpen, setIsOpen] = useState(false)
    // eslint-disable-next-line no-undef
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const startTimeout = (): void => {
        if (!duration) return
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false)
        }, duration)
    }

    const stopTimeout = (): void => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }

    useEffect(() => {
        setIsOpen(true)
        if (!duration || timeoutRef.current) return
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false)
        }, duration)
    }, [duration])

    return (
        <Drawer
            ref={ref}
            open={isOpen}
            closedClass="scale-75 opacity-0"
            className={clsx('pointer-events-auto duration-300 ease-in-out', className)}
            {...otherProps}
        >
            <Alert
                onMouseEnter={stopTimeout}
                onMouseLeave={startTimeout}
                className="mx-4 mb-4 min-w-64"
                type={type}
                variant={variant}
                {...(duration
                    ? {}
                    : {
                          onCloseClick: () => {
                              setIsOpen(false)
                          },
                      })}
            >
                {children}
            </Alert>
        </Drawer>
    )
})

Toast.displayName = 'Toast'

export { Toast, type ToastProps }
